import { useCustomMutation } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { COMMAND_FIELDS } from './model';

/**
 * E47 · the hub's mode command — ConfigureModeSet with the client (`subject` = the client; needs a granted relation) — ONE
 * deliberate act (a Save button), posted with `errorNotification: false` so the screen reports the outcome itself (portal
 * conventions): a CODED refusal (`modes.errors.MODE_*`, already in the therapist's language) is shown verbatim; a null
 * answer is a refusal, never a save; anything else is the screen's own copy plus Sentry.
 */
export type ModesCommandResult =
  | { ok: true; id: string | null; recorded: boolean }
  | { ok: false; code: string | undefined; message: string };

export const useModesCommand = () => {
  const { mutateAsync, isLoading } = useCustomMutation<{
    id?: string;
    recorded: boolean;
  }>();

  const configureModeSet = useCallback(
    async (
      input: Record<string, unknown>,
      fallback: string,
    ): Promise<ModesCommandResult> => {
      try {
        const res = await mutateAsync({
          url: '',
          method: 'post',
          values: {},
          errorNotification: false,
          successNotification: false,
          meta: {
            operation: 'configureModeSet',
            fields: COMMAND_FIELDS,
            variables: {
              input: {
                name: 'input',
                type: 'ConfigureModeSetInput!',
                value: input,
              },
            },
          },
        });
        const data = res?.data;
        if (!data) {
          return { ok: false, code: undefined, message: fallback };
        }
        return { ok: true, id: data.id ?? null, recorded: !!data.recorded };
      } catch (error) {
        const info = graphqlErrorInfo(error);
        if (info.code && info.message) {
          return { ok: false, code: info.code, message: info.message };
        }
        Sentry.captureException(error);
        return { ok: false, code: info.code, message: fallback };
      }
    },
    [mutateAsync],
  );

  return { configureModeSet, isLoading };
};
